import * as XLSX from "xlsx";
import { getRawMaterialPurchasesExport } from "../../services/RawMaterials/RawMaterialService";
import {
  RawMaterialPurchaseListDTO,
  RawMaterialPurchaseFilterExportDTO,
} from "../../models/RawMaterials/RawMaterial-model";

const parseNumber = (val: any) => {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const n = parseFloat(val.replace(/[^0-9.-]/g, ""));
    return isNaN(n) ? 0 : n;
  }
  return 0;
};

const approxEqual = (a: number, b: number, eps = 1e-6) =>
  Math.abs(a - b) < eps;

// ✅ Compute QC status NAME (no dependency on ID)
const getQcStatusNameByComputation = (item: RawMaterialPurchaseListDTO) => {
  const invoiced = parseNumber(item.invoicedQuantity ?? item.quantityWithUnit);
  const accepted = parseNumber(item.acceptedQuantity ?? item.acceptedQuantityWithUnit);
  const rejected = parseNumber(item.rejectedQuantity ?? item.rejectedQuantityWithUnit);
  const remainingFromApi = parseNumber(item.remainingQuantity ?? item.remainingQuantityWithUnit);

  const sumAR = accepted + rejected;
  const computedRemaining = Math.max(0, invoiced - sumAR);
  const remaining = remainingFromApi > 0 ? remainingFromApi : computedRemaining;

  // ✅ Check if no QC has been done yet (both accepted and rejected are 0)
  if (accepted === 0 && rejected === 0 && invoiced > 0) {
    return "QC Pending";
  }

  let statusName = "QC Pending";

  if (approxEqual(invoiced, accepted) && invoiced > 0) {
    statusName = "Accepted";
  } else if (approxEqual(invoiced, rejected) && invoiced > 0) {
    statusName = "Rejected";
  } else if (approxEqual(invoiced, sumAR) && invoiced > 0) {
    statusName = "Accepted & Rejected";
  } else if (approxEqual(invoiced, remaining) && invoiced > 0) {
    statusName = "QC Pending";
  } else if (approxEqual(accepted, 0) && rejected > 0 && !approxEqual(rejected, invoiced)) {
    statusName = "Partially Rejected";
  } else if (accepted > 0 && sumAR < invoiced) {
    statusName = "Partially Accepted";
  } else {
    statusName = "Partially Accepted";
  }

  return statusName;
};

// ✅ Get the actual display name from API statuses (by NAME, not ID)
const mapQcStatusDisplayName = (computedName: string, qcStatuses: any[]) => {
  const found = qcStatuses?.find(
    (s: any) => s.name?.toLowerCase() === computedName.toLowerCase()
  );
  return found ? found.name : computedName;
};

// ✅ Prepare export data with conditional QC By and QC Date
const rawMaterialWiseData = (
  rawMaterialList: RawMaterialPurchaseListDTO[],
  qcStatuses: any[]
) => {
  return rawMaterialList.map((item) => {
    const computedQcName = getQcStatusNameByComputation(item);
    const qcStatus = mapQcStatusDisplayName(computedQcName, qcStatuses);

    // ✅ Show "-" for QC By and QC Date if status is QC Pending
    const isQcPending = qcStatus.toLowerCase() === "qc pending";

    return {

            "GRN No": item.rawMaterialPurchaseId,
      "GRN report Date": item.purchaseDate
        ? new Date(item.purchaseDate).toLocaleDateString("en-GB")
        : "",
    //   Action: qcStatus,
     
      "Raw Material": item.rawMaterialName,
      Category: item.categoryName,
      "Invoice Number": item.invoiceId,
      Vendor: item.vendorName,
      "Invoiced Quantity": item.quantityWithUnit ?? item.invoicedQuantity ?? "0 Kgs",
      "Unit": item.posAlias,
      "Accepted Quantity": item.acceptedQuantityWithUnit ?? item.acceptedQuantity ?? "0 Kgs",
      "Remaining Quantity": item.remainingQuantityWithUnit ?? item.remainingQuantity ?? "0 Kgs",
      "Rejected Quantity": item.rejectedQuantityWithUnit ?? item.rejectedQuantity ?? "0 Kgs",
      "Base Price": parseNumber(item.basicAmount),
      "Tax Amount": parseNumber(item.taxAmount),
      "Total Amount": parseNumber(item.totalAmount),
      "QC By": isQcPending ? "-" : (item.updatedByName || "-"),
      "QC Date": isQcPending ? "-" : (item.updatedDate
        ? new Date(item.updatedDate).toLocaleDateString("en-GB")
        : "-"),
    };
  });
};

// ✅ Export function
export const GRNReportsExport = async (
  filter: RawMaterialPurchaseFilterExportDTO
) => {
  try {
    const data = await getRawMaterialPurchasesExport(filter);
    let rawMaterialList = data.rawMaterialPurchases || [];
    const qcStatuses = data.qcStatuses || [];

    if (!rawMaterialList.length) {
      console.warn("No data available for export.");
      return;
    }

    // ✅ SORT BY createdDate DESCENDING (newest first) before exporting
    rawMaterialList = [...rawMaterialList].sort((a, b) => {
      const dateA = new Date(a.createdDate || 0).getTime();
      const dateB = new Date(b.createdDate || 0).getTime();
      return dateB - dateA; // Descending order
    });

    const orderData = rawMaterialWiseData(rawMaterialList, qcStatuses);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(orderData);
    
    // ✅ Set column widths for better readability
    const columnWidths = [
      { wch: 15 }, // Purchase Date
      { wch: 20 }, // Action
      { wch: 20 }, // Raw Material
      { wch: 15 }, // Category
      { wch: 15 }, // Invoice Number
      { wch: 25 }, // Vendor
      { wch: 18 }, // Invoiced Quantity
      { wch: 20 }, // Unit
      { wch: 18 }, // Accepted Quantity
      { wch: 18 }, // Remaining Quantity
      { wch: 18 }, // Rejected Quantity
      { wch: 15 }, // Base Price
      { wch: 15 }, // Tax Amount
      { wch: 15 }, // Total Amount
      { wch: 20 }, // QC By
      { wch: 15 }, // QC Date
    ];
    ws["!cols"] = columnWidths;

    XLSX.utils.book_append_sheet(wb, ws, "RawMaterials");
    XLSX.writeFile(wb, "GRNReportExport.xlsx");

    console.log(`✅ Exported ${orderData.length} records successfully.`);
  } catch (error) {
    console.error("❌ Error exporting raw material list:", error);
  }
};
